// ============================================================================
// New Ticket — support opening a ticket on a creator's behalf
// ============================================================================
// Every ticket used to start with the customer. This is the other direction:
// support reaching out first (a failed payout, a flagged video, a stalled
// hire) without asking the creator to file a request they never asked for.
//
// The ticket this creates lands at Open with an EMPTY thread. The first
// message the agent sends in /ticket-chat/:id is what moves it to In Progress
// — see migration 20260919000000_support_admin_created_tickets.sql. That is
// why this dialog has no "first message" field: the message belongs in the
// conversation, where the creator answers it, not in a create form.
//
// Creators only, for now. The queue's chat screen itself works fine for a
// brand ticket (Feedback.tsx's openTicket no longer gates on source) — this
// dialog is narrower because support_admin_open_ticket() is still hardcoded
// to source: 'creator' server-side (see migration
// 20260917000000_support_admin_created_tickets.sql), and searchCustomers()
// below only searches creator profiles. Widening both to also open a ticket
// on a BRAND's behalf is a separate follow-up.
// ============================================================================

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Search, Loader2, X, Headset } from 'lucide-react';
import { toast } from 'sonner';
import {
  adminOpenTicket, searchCustomers, customerLabel,
  TICKET_TYPES, TICKET_PRIORITIES, TYPE_LABELS, PRIORITY_LABELS,
  type AdminOpenedTicket, type SupportCustomer, type TicketType, type TicketPriority,
} from '@/lib/support';

const MAX_SUBJECT = 160;
const MAX_MESSAGE = 4000;

export function NewTicketDialog({ open, onOpenChange, onCreated }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (ticket: AdminOpenedTicket) => void;
}) {
  const [customer, setCustomer] = useState<SupportCustomer | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [type, setType] = useState<TicketType>('support');
  const [priority, setPriority] = useState<TicketPriority>('normal');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) return;
    setCustomer(null);
    setSearchInput('');
    setSearch('');
    setType('support');
    setPriority('normal');
    setSubject('');
    setMessage('');
    setError(null);
  }, [open]);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data: results, isLoading: searching } = useQuery({
    queryKey: ['support-customer-search', search],
    queryFn: () => searchCustomers(search),
    enabled: open && !customer,
  });

  const blocker = !customer
    ? 'Pick the creator this ticket is for'
    : !subject.trim()
      ? 'Give the ticket a subject'
      : !message.trim()
        ? 'Describe what this ticket is about'
        : null;

  async function submit() {
    if (!customer || blocker) return;
    setSubmitting(true);
    setError(null);
    try {
      const ticket = await adminOpenTicket({
        userId: customer.id,
        type,
        subject: subject.trim(),
        message: message.trim(),
        priority,
      });
      toast.success(
        `Ticket #${ticket.ticketNumber ?? '—'} opened for ${customerLabel(customer)}`,
        { description: 'Send the first message to move it to In Progress.' },
      );
      onCreated(ticket);
      onOpenChange(false);
    } catch (e: any) {
      setError(e?.message ?? 'Could not open the ticket');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!submitting) onOpenChange(o); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Headset className="h-4 w-4 text-primary" />
            New ticket
          </DialogTitle>
          <DialogDescription>
            Opens a ticket on a creator's behalf. It starts at <strong>Open</strong> with an empty
            chat — your first message in the thread moves it to <strong>In Progress</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* ── Creator ─────────────────────────────────────────────────── */}
          <div className="space-y-1.5">
            <label htmlFor="ntd-customer" className="text-sm font-medium">Creator</label>
            {customer ? (
              <div className="flex items-center gap-2 rounded-lg border bg-muted/30 p-2">
                <Avatar className="h-7 w-7">
                  <AvatarFallback className="bg-gradient-to-br from-violet-500 to-fuchsia-500 text-[10px] font-bold text-white">
                    {customerLabel(customer).slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{customerLabel(customer)}</p>
                  <p className="truncate text-xs text-muted-foreground">{customer.email || '—'}</p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0"
                  aria-label="Pick a different creator"
                  onClick={() => { setCustomer(null); setError(null); }}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="ntd-customer"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Search by handle, name or email…"
                    className="pl-9"
                    autoFocus
                  />
                </div>
                <div className="max-h-52 space-y-1 overflow-y-auto rounded-lg border p-1">
                  {searching ? (
                    <div className="space-y-1 p-1">
                      {[0, 1, 2].map((i) => <Skeleton key={i} className="h-9 w-full rounded-md" />)}
                    </div>
                  ) : !results || results.length === 0 ? (
                    <p className="p-3 text-center text-xs text-muted-foreground">
                      {search.trim() ? 'No creators match that search' : 'No creators found'}
                    </p>
                  ) : (
                    results.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => { setCustomer(c); setError(null); }}
                        className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-muted"
                      >
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium">{customerLabel(c)}</span>
                          <span className="block truncate text-xs text-muted-foreground">{c.email || '—'}</span>
                        </span>
                      </button>
                    ))
                  )}
                </div>
              </>
            )}
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label htmlFor="ntd-type" className="text-sm font-medium">Type</label>
              <Select value={type} onValueChange={(v) => setType(v as TicketType)}>
                <SelectTrigger id="ntd-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TICKET_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>{TYPE_LABELS[t]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label htmlFor="ntd-priority" className="text-sm font-medium">Priority</label>
              <Select value={priority} onValueChange={(v) => setPriority(v as TicketPriority)}>
                <SelectTrigger id="ntd-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TICKET_PRIORITIES.map((p) => (
                    <SelectItem key={p} value={p}>{PRIORITY_LABELS[p]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="ntd-subject" className="text-sm font-medium">Subject</label>
            <Input
              id="ntd-subject"
              value={subject}
              onChange={(e) => { setSubject(e.target.value.slice(0, MAX_SUBJECT)); setError(null); }}
              placeholder="Payout for your September hire needs your bank details"
              maxLength={MAX_SUBJECT}
            />
            <p className="text-[11px] text-muted-foreground">{subject.length}/{MAX_SUBJECT} characters</p>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="ntd-message" className="text-sm font-medium">
              Description <span className="font-normal text-muted-foreground">— the creator sees this</span>
            </label>
            <Textarea
              id="ntd-message"
              value={message}
              onChange={(e) => { setMessage(e.target.value.slice(0, MAX_MESSAGE)); setError(null); }}
              rows={4}
              placeholder="What this ticket is about, in the creator's words…"
              maxLength={MAX_MESSAGE}
            />
            <p className="text-[11px] text-muted-foreground">
              Shown as the request's preview in the creator's app until you send the first message.
              Keep internal context for the ticket note or an internal thread message.
            </p>
          </div>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-800">
              <p className="font-semibold">Ticket not created</p>
              <p className="mt-0.5">{error}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" disabled={submitting} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!!blocker || submitting} onClick={submit}>
            {submitting
              ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Opening…</>
              : 'Open ticket'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
